<!--
  Support, drawn as the thing it watches: your application's heartbeat running
  across, sixty days of uptime under it, and the two response promises pinned
  to the line. A bright pulse keeps travelling along the beat.
-->
<template>
  <VisualStage glow="bg-green-400/10">
    <svg ref="root" viewBox="0 0 560 420" class="relative mx-auto block w-full max-w-[560px]" :class="inView ? 'is-in' : ''" role="img" aria-label="Your application's heartbeat with sixty days of uptime: critical issues answered within four hours, everything else within one working day">
      <!-- Status -->
      <circle cx="34" cy="52" r="5" class="fill-green-400">
        <animate attributeName="opacity" values="1;0.35;1" dur="2s" repeatCount="indefinite" />
      </circle>
      <text x="50" y="57" class="fill-slate-800 text-[15px] font-medium dark:fill-white">All systems operational</text>

      <!-- The heartbeat: the trace, then a bright pulse running along it. -->
      <path :d="beat" pathLength="1000" class="trace" />
      <path :d="beat" pathLength="1000" class="pulse" />

      <!-- The promises, pinned to the beat. -->
      <g v-for="pin in pins" :key="pin.label" class="pin" :style="{ '--d': pin.delay }">
        <circle :cx="pin.x" :cy="pin.y" r="4" class="fill-[#f9fafb] stroke-green-400 dark:fill-dark-500" stroke-width="2" />
        <line :x1="pin.x" :y1="pin.y - 6" :x2="pin.x" :y2="pin.ly + 8" class="stroke-slate-400/60 dark:stroke-white/25" />
        <text :x="pin.x" :y="pin.ly - 14" text-anchor="middle" class="fill-slate-800 text-[22px] font-semibold dark:fill-white">{{ pin.value }}</text>
        <text :x="pin.x" :y="pin.ly + 4" text-anchor="middle" class="fill-slate-500 text-[12px] dark:fill-white/55">{{ pin.label }}</text>
      </g>

      <!-- Sixty days of uptime. -->
      <g class="ticks">
        <rect v-for="(day, i) in days" :key="i" :x="34 + i * 8.25" y="330" width="5" height="30" rx="1" :class="day === 'ok' ? 'fill-green-400/75' : 'fill-amber-400'" class="tick" :style="{ '--i': i }" />
      </g>
      <text x="34" y="384" class="fill-slate-500 text-[12px] dark:fill-white/45">Uptime, last 60 days</text>
      <text x="526" y="384" text-anchor="end" class="fill-slate-500 text-[12px] dark:fill-white/45">Monitored around the clock</text>
    </svg>
  </VisualStage>
</template>

<script setup lang="ts">
// A beat every 150 units along the baseline, a small wave then the spike,
// phased so the spikes land at 130, 280 and 430: either side of the middle,
// with room for a promise centred over each outer one.
const beat = (() => {
  let d = 'M -100 230'
  for (let x = -100; x < 560; x += 150) {
    d += ` L ${x + 40} 230 l 10 -9 l 10 9 L ${x + 72} 230 L ${x + 80} 168 L ${x + 89} 262 L ${x + 97} 230 L ${x + 150} 230`
  }
  return d
})()

const pins = [
  { x: 130, y: 168, ly: 112, value: '4 hours', label: 'to answer anything critical', delay: '900ms' },
  { x: 430, y: 168, ly: 112, value: '1 working day', label: 'for standard requests', delay: '1150ms' },
]

// Sixty days, one of them with a blip.
const days = Array.from({ length: 60 }, (_, i) => (i === 41 ? 'warn' : 'ok'))

const root = ref<SVGElement>()
const inView = ref(false)
onMounted(() => {
  if (!root.value) return
  const observer = new IntersectionObserver(([entry]) => {
    if (!entry?.isIntersecting) return
    inView.value = true
    observer.disconnect()
  }, { threshold: 0.3 })
  observer.observe(root.value)
})
</script>

<style scoped>
.trace {
  fill: none;
  stroke: rgb(74 222 128 / 0.35);
  stroke-width: 1.5;
  stroke-linejoin: round;
  stroke-dasharray: 1000;
  stroke-dashoffset: 1000;
  transition: stroke-dashoffset 1600ms cubic-bezier(0.65, 0, 0.35, 1) 200ms;
}
.is-in .trace {
  stroke-dashoffset: 0;
}
.pulse {
  fill: none;
  stroke: #4ade80;
  stroke-width: 2.25;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 70 930;
  stroke-dashoffset: 70;
  opacity: 0;
  filter: drop-shadow(0 0 6px rgb(74 222 128 / 0.8));
}
.is-in .pulse {
  opacity: 1;
  transition: opacity 400ms ease-out 1600ms;
  animation: travel 3.2s linear 1600ms infinite;
}
@keyframes travel {
  from { stroke-dashoffset: 70; }
  to { stroke-dashoffset: -930; }
}

.pin {
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 500ms ease-out, transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: var(--d);
}
.is-in .pin {
  opacity: 1;
  transform: none;
}

.tick {
  transform-box: fill-box;
  transform-origin: bottom;
  transform: scaleY(0.15);
  transition: transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
  transition-delay: calc(var(--i) * 12ms + 400ms);
}
.is-in .tick {
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .trace { stroke-dashoffset: 0; transition: none; }
  .pulse { display: none; }
  .pin { opacity: 1; transform: none; transition: none; }
  .tick { transform: none; transition: none; }
}
</style>
